/**
 * Tool Search Logging
 *
 * Provides structured logging for tool search operations.
 * Helps with debugging and monitoring tool search behavior.
 *
 * All output is routed through the shared `@west-tennessee-consulting/logger` package logger so every
 * entry inherits baseline JSON context (service, env, runtime, deployment, etc.).
 * The exported function signatures are kept stable so callers are unaffected.
 */

import { logger } from '../../logger.js';

interface LogContext {
  requestId?: string;
  chatId?: string;
  userId?: string;
  [key: string]: unknown;
}

/**
 * Log tool search query
 */
export function logToolSearch(
  query: string,
  variant: 'regex' | 'bm25',
  context?: LogContext
): void {
  logger.debug('tool search', {
    op: 'tool_search',
    query,
    variant,
    ...context,
  });
}

/**
 * Log tool discovery
 */
export function logToolDiscovery(
  discoveredTools: string[],
  query: string,
  context?: LogContext
): void {
  logger.debug('tool discovery', {
    op: 'tool_discovery',
    discoveredTools,
    discoveredCount: discoveredTools.length,
    query,
    ...context,
  });
}

/**
 * Log tool execution
 */
export function logToolExecution(
  toolId: string,
  success: boolean,
  duration: number,
  error?: Error,
  context?: LogContext
): void {
  if (success) {
    logger.debug('tool execution', {
      op: 'tool_execution',
      toolId,
      success,
      durationMs: duration,
      ...context,
    });
  } else {
    logger.error('tool execution failed', error, {
      op: 'tool_execution',
      toolId,
      success,
      durationMs: duration,
      ...context,
    });
  }
}

/**
 * Log tool search error
 */
export function logToolSearchError(
  error: Error,
  context?: LogContext
): void {
  logger.error('tool search error', error, {
    op: 'tool_search_error',
    ...context,
  });
}

